import Layout from "@/components/Layout"
import { Link } from "react-router-dom"
import {Calculator, TrendingDown, Building, Home as HomeIcon, Laptop, Armchair, Truck, Car, ArrowRight} from 'lucide-react'

const coreCalcs = [
  {
    title: "Straight Line Method Amortization Calculator",
    desc: "The most widely used method — evenly spreads asset cost over useful life.",
    href: "/calculator/straight-line",
    icon: Calculator,
    color: "bg-blue-600",
    badge: "Most Popular",
  },
  {
    title: "Double Declining Balance Calculator",
    desc: "Accelerated depreciation — twice the straight line rate applied to book value.",
    href: "/calculator/double-declining",
    icon: TrendingDown,
    color: "bg-indigo-600",
    badge: "Accelerated",
  },
  {
    title: "MACRS Tax Depreciation Calculator",
    desc: "IRS Modified Accelerated Cost Recovery System with official GDS tables.",
    href: "/calculator/macrs",
    icon: Building,
    color: "bg-blue-700",
    badge: "IRS Tax",
  },
  {
    title: "Rental Property & Real Estate Depreciation",
    desc: "Residential (27.5-yr) and commercial (39-yr) real estate depreciation.",
    href: "/calculator/rental-property",
    icon: HomeIcon,
    color: "bg-cyan-600",
    badge: "Real Estate",
  },
]

const assetCalcs = [
  {
    title: "Computer Depreciation & Hardware Life Calculator",
    desc: "IT equipment, servers, software — 5-year MACRS property with DDB method.",
    href: "/calculator/computer-hardware",
    icon: Laptop,
    color: "text-violet-600",
    bg: "bg-violet-50",
  },
  {
    title: "Furniture & Appliance Depreciation Tools",
    desc: "Office furniture (7-yr) and commercial appliances (5-yr) depreciation.",
    href: "/calculator/furniture-appliances",
    icon: Armchair,
    color: "text-orange-600",
    bg: "bg-orange-50",
  },
  {
    title: "Tractor, Trailer & Heavy Equipment Calculator",
    desc: "Tractors, semi-trucks, construction and agricultural equipment.",
    href: "/calculator/heavy-equipment",
    icon: Truck,
    color: "text-amber-600",
    bg: "bg-amber-50",
  },
  {
    title: "Business Vehicle & IRS Auto Depreciation",
    desc: "Cars, trucks, SUVs with luxury auto Section 280F limits applied.",
    href: "/calculator/business-vehicles",
    icon: Car,
    color: "text-teal-600",
    bg: "bg-teal-50",
  },
]

export default function Calculators() {
  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">All Depreciation Calculators</h1>
          <p className="text-lg text-gray-600">
            Professional-grade, free depreciation tools for every asset class and IRS method.
            Choose a calculator below to generate a full depreciation schedule with export.
          </p>
        </div>

        {/* Core Calculators */}
        <section className="mb-14">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Core Depreciation Methods</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {coreCalcs.map((c) => {
              const Icon = c.icon
              return (
                <Link key={c.href} to={c.href}
                  className="group bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg hover:border-blue-300 transition-all">
                  <div className="flex items-start gap-4">
                    <div className={`${c.color} rounded-lg p-3 group-hover:scale-105 transition-transform`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-base font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                          {c.title}
                        </h3>
                      </div>
                      <span className="inline-block text-xs font-medium bg-blue-100 text-blue-700 rounded px-2 py-0.5 mb-2">
                        {c.badge}
                      </span>
                      <p className="text-sm text-gray-600">{c.desc}</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
                  </div>
                </Link>
              )
            })}
          </div>
        </section>

        {/* Asset Specific Tools */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Asset Specific Tools</h2>
          <p className="text-gray-600 mb-6">
            Specialized calculators targeting individual asset categories with tailored
            IRS rules, useful life tables, and SEO-optimized educational content.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {assetCalcs.map((c) => {
              const Icon = c.icon
              return (
                <Link key={c.href} to={c.href}
                  className="group bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md hover:border-gray-300 transition-all flex items-start gap-4">
                  <div className={`${c.bg} rounded-lg p-3 group-hover:scale-105 transition-transform`}>
                    <Icon className={`w-6 h-6 ${c.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-base font-semibold text-gray-900 mb-1 group-hover:${c.color} transition-colors`}>
                      {c.title}
                    </h3>
                    <p className="text-sm text-gray-600">{c.desc}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
                </Link>
              )
            })}
          </div>
        </section>

        {/* Export CTA Banner */}
        <div className="mt-12 bg-green-50 border border-green-200 rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-1">
            <h3 className="font-semibold text-green-900 mb-1">Export to Excel / CSV</h3>
            <p className="text-sm text-green-700">
              Every calculator generates a full depreciation schedule you can export as a CSV
              file — compatible with Microsoft Excel and Google Sheets for tax filing and
              accounting records.
            </p>
          </div>
          <Link to="/calculator/straight-line"
            className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors whitespace-nowrap">
            Try a Calculator
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </Layout>
  )
}
